import { useEffect, useState } from "react";
import { currentEvent } from "./season";

// Arcade-style sound effects made on the fly with the Web Audio API, so there
// are no audio files to load. Muting is remembered per browser.
const MUTE_KEY = "partycade.muted";

let ctx = null;
let muted = false;
try {
  muted = localStorage.getItem(MUTE_KEY) === "1";
} catch {}
const listeners = new Set();

export function setMuted(value) {
  muted = value;
  try {
    localStorage.setItem(MUTE_KEY, value ? "1" : "0");
  } catch {}
  listeners.forEach((fn) => fn(value));
  if (!value) playSound("boing");
}

export function useMuted() {
  const [value, setValue] = useState(muted);
  useEffect(() => {
    listeners.add(setValue);
    return () => listeners.delete(setValue);
  }, []);
  return [value, setMuted];
}

// The sounds are in an "arcade" style: chiptune square-wave blips and quick
// runs of notes, a triangle-wave bass, noise for drums and dice, and a short
// echo and a little room reverb on everything. All of it goes through one
// compressor so stacked sounds don't clip.

// Built the first time a sound plays: master volume, compressor, echo and reverb.
let bus = null;
function getBus() {
  if (bus) return bus;
  const comp = ctx.createDynamicsCompressor();
  comp.threshold.value = -14;
  comp.ratio.value = 4;
  comp.attack.value = 0.003;
  comp.release.value = 0.15;
  const master = ctx.createGain();
  master.gain.value = 0.8;
  master.connect(comp).connect(ctx.destination);
  const echo = ctx.createGain();
  echo.gain.value = 0.3;
  const delay = ctx.createDelay(1);
  delay.delayTime.value = 0.14;
  const feedback = ctx.createGain();
  feedback.gain.value = 0.32;
  const tone = ctx.createBiquadFilter();
  tone.type = "lowpass";
  tone.frequency.value = 3000;
  echo.connect(delay);
  delay.connect(tone).connect(feedback).connect(delay);
  tone.connect(master);
  const noise = ctx.createBuffer(1, ctx.sampleRate * 2, ctx.sampleRate);
  const data = noise.getChannelData(0);
  for (let i = 0; i < data.length; i++) data[i] = Math.random() * 2 - 1;
  // The reverb's echo of a room: 2.2 seconds of fading noise.
  const verb = ctx.createConvolver();
  const len = Math.floor(ctx.sampleRate * 2.2);
  const room = ctx.createBuffer(2, len, ctx.sampleRate);
  for (let c = 0; c < 2; c++) {
    const d = room.getChannelData(c);
    for (let i = 0; i < len; i++) d[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / len, 3.2);
  }
  verb.buffer = room;
  const reverb = ctx.createGain();
  reverb.gain.value = 0.12;
  reverb.connect(verb).connect(master);
  bus = { master, echo, reverb, noise };
  return bus;
}

// A voice's volume shape: quick attack, decay to `s` (a share of the peak),
// hold there, then release. Returns when it ends.
function envelope(param, t, { a = 0.002, d = 0.02, s = 0.8, hold = 0, r = 0.03 }) {
  param.setValueAtTime(0.0001, t);
  param.exponentialRampToValueAtTime(1, t + a);
  const level = Math.max(0.0001, s);
  param.exponentialRampToValueAtTime(level, t + a + d);
  if (hold) param.setValueAtTime(level, t + a + d + hold);
  param.exponentialRampToValueAtTime(0.0001, t + a + d + hold + r);
  return t + a + d + hold + r;
}

function output(node, vol) {
  const { master, echo, reverb } = getBus();
  const gain = ctx.createGain();
  gain.gain.value = vol;
  node.connect(gain);
  gain.connect(master);
  gain.connect(echo);
  gain.connect(reverb);
}

// One tone. Options: type (waveform), vol, to (slide to this pitch, over
// `slide` seconds or the whole note), vib ([speed, depth] in Hz), detune
// (cents), filter ({ type, freq, to }, a low-pass by default), env (see envelope).
function tone(at, freq, { type = "square", vol = 0.06, to, slide, vib, detune, filter, env = {} } = {}) {
  const t = ctx.currentTime + at;
  const osc = ctx.createOscillator();
  osc.type = type;
  osc.frequency.setValueAtTime(freq, t);
  if (detune) osc.detune.value = detune;
  const gain = ctx.createGain();
  const end = envelope(gain.gain, t, env);
  if (to) osc.frequency.exponentialRampToValueAtTime(to, slide ? t + slide : end);
  if (vib) {
    const lfo = ctx.createOscillator();
    const depth = ctx.createGain();
    lfo.frequency.value = vib[0];
    depth.gain.value = vib[1];
    lfo.connect(depth).connect(osc.frequency);
    lfo.start(t);
    lfo.stop(end + 0.05);
  }
  let node = osc;
  if (filter) {
    node = ctx.createBiquadFilter();
    node.type = filter.type || "lowpass";
    node.frequency.setValueAtTime(filter.freq, t);
    if (filter.to) node.frequency.exponentialRampToValueAtTime(filter.to, end);
    osc.connect(node);
  }
  node.connect(gain);
  output(gain, vol);
  osc.start(t);
  osc.stop(end + 0.05);
}

// A square-wave blip held for `len` seconds; the bass is a triangle wave.
const blip = (at, freq, len, vol = 0.06, opts = {}) => tone(at, freq, { vol, ...opts, env: { hold: len, ...opts.env } });
const bass = (at, freq, len, vol = 0.25) => tone(at, freq, { type: "triangle", vol, env: { d: 0.05, hold: len, r: 0.05 } });

// Filtered noise, for drums, dice, crashes and whooshes. filter: "highpass",
// "lowpass" or "bandpass". `env` swaps the short hit for a slower shape.
function noise(at, { filter = "highpass", freq = 3000, q = 1, to, vol = 0.06, len = 0.03, env }) {
  const t = ctx.currentTime + at;
  const src = ctx.createBufferSource();
  src.buffer = getBus().noise;
  const f = ctx.createBiquadFilter();
  f.type = filter;
  f.frequency.setValueAtTime(freq, t);
  f.Q.value = q;
  const gain = ctx.createGain();
  const end = envelope(gain.gain, t, env || { a: 0.001, d: len, s: 0.0001, r: 0.01 });
  if (to) f.frequency.exponentialRampToValueAtTime(to, end);
  src.connect(f).connect(gain);
  output(gain, vol);
  src.start(t, Math.random());
  src.stop(end + 0.05);
}

// A plucked square note that rings out over `len` seconds, and a kick drum.
const pluck = (at, freq, vol = 0.06, len = 0.25) => tone(at, freq, { vol, env: { d: len, s: 0.0001, r: 0.05 } });
const kick = (at, vol) => tone(at, 150, { type: "sine", vol, to: 45, slide: 0.12, env: { d: 0.25, s: 0.0001, r: 0.05 } });

// Quick notes one after another, `gap` seconds apart.
const run = (at, freqs, gap, len, vol) => freqs.forEach((f, i) => blip(at + i * gap, f, len, vol));

// The building blocks, for the holiday sound sets in lib/eventSounds.js.
export { tone, noise, bass, kick };

const SOUNDS = {
  // Buttons
  pop: () => tone(0, 900, { vol: 0.06, to: 180, env: { d: 0.07, s: 0.0001, r: 0.05 } }),
  bloop: () => run(0, [784, 1047], 0.035, 0.025, 0.05),
  // A springy boing.
  boing: () => {
    const env = { a: 0.004, d: 0.3, s: 0.0001, r: 0.05 };
    tone(0, 190, { vol: 0.05, to: 420, vib: [16, 70], env });
    tone(0, 190, { type: "triangle", vol: 0.12, to: 420, vib: [16, 70], env });
  },
  squeak: () => run(0, [2093, 2637], 0.025, 0.018, 0.04),
  honk: () => {
    blip(0, 220, 0.09, 0.07);
    blip(0, 233, 0.09, 0.04);
    blip(0.14, 220, 0.12, 0.07);
    blip(0.14, 233, 0.12, 0.04);
  },
  hmm: () => tone(0, 440, { type: "triangle", vol: 0.2, to: 392, vib: [8, 12], env: { hold: 0.25 } }),
  slideUp: () => run(0, [523, 659, 784, 1047], 0.04, 0.03, 0.06),
  slideDown: () => run(0, [1047, 784, 659, 523], 0.05, 0.04, 0.06),

  // Game moments
  // A whistle slides up, then a chord.
  start: () => {
    tone(0, 600, { vol: 0.04, to: 1800, slide: 0.3, vib: [6, 25], env: { a: 0.02, d: 0.05, hold: 0.22, r: 0.04 } });
    [523, 659, 784].forEach((f) => pluck(0.34, f, 0.035, 0.6));
    pluck(0.34, 1047, 0.045, 0.8);
    tone(0.34, 262, { type: "triangle", vol: 0.25, env: { a: 0.003, d: 0.1, s: 0.7, hold: 0.3, r: 0.1 } });
  },
  yourTurn: () => run(0, [659, 784, 988, 1319, 1568], 0.045, 0.04, 0.06),
  nextTurn: () => run(0, [1047, 784], 0.04, 0.03, 0.04),
  question: () => run(0, [659, 880], 0.09, 0.06, 0.06),
  answer: () => blip(0, 1319, 0.025, 0.05),
  // Three notes climbing, with a sparkle on top.
  correct: () => {
    pluck(0, 784);
    pluck(0.09, 1047);
    pluck(0.18, 1319, 0.065, 0.6);
    run(0.18, [2637, 3136], 0.05, 0.03, 0.03);
  },
  solvedMe: () => {
    run(0, [523, 659, 784, 1047], 0.06, 0.05, 0.06);
    run(0.26, [1319, 1568, 2093, 2637], 0.05, 0.04, 0.05);
    blip(0.5, 2093, 0.3, 0.05);
    bass(0.26, 262, 0.5);
  },
  // The sad trombone: wah, wah, wah, waaah.
  wrong: () => {
    [
      [233, 0],
      [220, 0.28],
      [208, 0.56],
    ].forEach(([f, at]) =>
      tone(at, f, { vol: 0.06, to: f * 0.97, filter: { freq: 1400 }, env: { a: 0.01, d: 0.05, hold: 0.18, r: 0.04 } }),
    );
    tone(0.84, 196, { vol: 0.06, vib: [6, 7], filter: { freq: 1400 }, env: { a: 0.01, d: 0.05, hold: 0.6, r: 0.1 } });
    tone(0, 98, { type: "triangle", vol: 0.2, env: { a: 0.01, d: 0.1, s: 0.7, hold: 1.3, r: 0.1 } });
  },
  skip: () => blip(0, 1047, 0.18, 0.06, { to: 262 }),
  // A whoosh rises, then a big chord lands.
  reveal: () => {
    noise(0, { filter: "bandpass", freq: 200, to: 6000, q: 1.2, vol: 0.12, env: { a: 0.6, d: 0.03, s: 0.0001, r: 0.03 } });
    tone(0, 110, { vol: 0.025, to: 880, env: { a: 0.5, d: 0.05, r: 0.05 } });
    tone(0.62, 110, { type: "triangle", vol: 0.35, to: 55, env: { a: 0.003, d: 0.5, s: 0.0001, r: 0.05 } });
    [784, 988, 1175, 1568].forEach((f) => pluck(0.62, f, 0.03, 1));
    noise(0.62, { freq: 5000, vol: 0.06, len: 0.6 });
  },
  // Dice rattling unevenly, then one last clack.
  roll: () => {
    [0, 0.05, 0.085, 0.15, 0.19, 0.27, 0.31, 0.4].forEach((at, i) => {
      noise(at, { filter: "bandpass", freq: 2600 + ((i * 733) % 1500), q: 6, vol: 0.14 - i * 0.008, len: 0.025 });
      blip(at, 900 + ((i * 377) % 600), 0.012, 0.025);
    });
    noise(0.5, { filter: "bandpass", freq: 1500, q: 5, vol: 0.18, len: 0.04 });
    blip(0.5, 523, 0.04, 0.05);
  },
  // Ting, ting!
  coin: () => {
    pluck(0, 2093, 0.05, 0.25);
    pluck(0.07, 2637, 0.055, 0.7);
  },
  // Wee-woo, wee-woo.
  siren: () => [880, 660, 880, 660].forEach((f, i) => blip(i * 0.16, f, 0.13, 0.05, { vib: [12, 15] })),
  sparkle: () => run(0, [1568, 2093, 2637, 3136], 0.04, 0.03, 0.04),
  // Spooky nightfall: wind, and an owl hooting twice.
  night: () => {
    noise(0, { filter: "bandpass", freq: 400, to: 900, q: 0.8, vol: 0.12, env: { a: 0.5, d: 0.6, s: 0.3, r: 0.6 } });
    tone(0.25, 392, { type: "sine", vol: 0.16, to: 370, vib: [5, 4], env: { a: 0.05, d: 0.15, s: 0.6, hold: 0.15, r: 0.2 } });
    tone(0.75, 330, { type: "sine", vol: 0.16, to: 311, vib: [5, 4], env: { a: 0.05, d: 0.2, s: 0.6, hold: 0.35, r: 0.4 } });
    noise(0.25, { filter: "bandpass", freq: 900, q: 3, vol: 0.03, env: { a: 0.05, d: 0.3, s: 0.0001, r: 0.05 } });
  },
  // Dun, dun, DUNNN: three heavy brass hits with a drum.
  dun: () => {
    [
      [196, 0, 0.16],
      [185, 0.28, 0.16],
      [165, 0.56, 0.9],
    ].forEach(([f, at, len]) => {
      for (const detune of [-10, 10]) {
        tone(at, f, { vol: 0.035, detune, filter: { freq: 600, to: 2200 }, env: { a: 0.02, d: 0.1, s: 0.7, hold: len, r: 0.12 } });
      }
      tone(at, f / 2, { type: "triangle", vol: 0.28, env: { a: 0.003, d: 0.05, hold: len, r: 0.15 } });
      kick(at, 0.4);
      noise(at, { filter: "lowpass", freq: 800, vol: 0.1, len: 0.12 });
    });
    noise(0.56, { freq: 4500, vol: 0.05, len: 1.4 });
  },
  boom: () => {
    noise(0, { filter: "lowpass", freq: 900, to: 80, vol: 0.35, len: 0.7 });
    tone(0, 110, { type: "triangle", vol: 0.4, to: 35, env: { d: 0.05, hold: 0.3, r: 0.3 } });
    blip(0, 98, 0.15, 0.06, { to: 49 });
  },
  // A swell, then a run of notes climbing up.
  podium: () => {
    noise(0, { filter: "bandpass", freq: 300, to: 5000, q: 1.2, vol: 0.09, env: { a: 0.3, d: 0.03, s: 0.0001, r: 0.03 } });
    [523, 659, 784, 1047].forEach((f) =>
      tone(0.3, f, { vol: 0.018, filter: { freq: 2500, to: 900 }, env: { a: 0.08, d: 0.3, s: 0.6, hold: 0.8, r: 0.8 } }),
    );
    [1047, 1319, 1568, 2093, 2637].forEach((f, i) => pluck(0.3 + i * 0.09, f, 0.04, 0.5));
    tone(0.3, 131, { type: "triangle", vol: 0.28, env: { a: 0.003, d: 0.1, s: 0.7, hold: 0.6, r: 0.3 } });
    kick(0.3, 0.35);
    noise(0.3, { freq: 6000, vol: 0.05, len: 1.2 });
  },
};

// Halloween versions of the sounds, played instead during Halloween (see
// lib/season.js); any sound not listed here stays as above. A church
// organ, a theremin-like ghost, cauldron bubbles, bats, crows, a wolf, bones
// and thunder.
const organ = (at, freq, len, vol = 0.03) => {
  for (const detune of [-8, 8]) {
    tone(at, freq, { vol, detune, filter: { freq: 1800 }, env: { a: 0.015, d: 0.08, s: 0.75, hold: len, r: 0.25 } });
  }
  tone(at, freq / 2, { type: "triangle", vol: vol * 5, env: { a: 0.015, d: 0.08, s: 0.75, hold: len, r: 0.25 } });
};
const chime = (at, freq, vol = 0.12, len = 0.8) => tone(at, freq, { type: "sine", vol, env: { d: len, s: 0.0001, r: 0.1 } });
const ghost = (at, freq, to, len, vol = 0.14) =>
  tone(at, freq, { type: "sine", vol, to, vib: [5.5, freq * 0.04], env: { a: 0.08, d: 0.1, s: 0.8, hold: len, r: 0.25 } });
const thunder = (at, vol = 0.35) => {
  noise(at, { filter: "lowpass", freq: 2500, to: 120, vol, env: { a: 0.005, d: 0.25, s: 0.35, hold: 0.2, r: 1.1 } });
  noise(at + 0.12, { filter: "lowpass", freq: 400, to: 60, vol: vol * 0.8, env: { a: 0.05, d: 0.4, s: 0.3, r: 0.9 } });
};
const bubble = (at, freq, vol = 0.16) => tone(at, freq, { type: "sine", vol, to: freq * 2.4, env: { a: 0.004, d: 0.06, s: 0.0001, r: 0.03 } });
const caw = (at) => tone(at, 640, { type: "sawtooth", vol: 0.05, to: 470, filter: { type: "bandpass", freq: 1300 }, env: { a: 0.01, d: 0.05, s: 0.7, hold: 0.12, r: 0.05 } });
// Whole-tone runs sound like a spell being cast.
const spell = (at, freqs, gap, vol = 0.1) => freqs.forEach((f, i) => chime(at + i * gap, f, vol, 0.35));

const HALLOWEEN = {
  // Buttons: cauldron bubbles, a ghostly wooo, bat squeaks, a crow.
  pop: () => bubble(0, 300),
  bloop: () => {
    bubble(0, 260, 0.13);
    bubble(0.05, 390, 0.11);
  },
  boing: () => ghost(0, 440, 660, 0.12, 0.13),
  squeak: () => {
    tone(0, 3600, { type: "sine", vol: 0.06, to: 2700, env: { d: 0.04, s: 0.0001, r: 0.01 } });
    tone(0.05, 3900, { type: "sine", vol: 0.05, to: 3000, env: { d: 0.04, s: 0.0001, r: 0.01 } });
  },
  honk: () => {
    caw(0);
    caw(0.2);
  },
  hmm: () => ghost(0, 330, 294, 0.25, 0.14),
  slideUp: () => spell(0, [523, 587, 659, 740], 0.05),
  slideDown: () => spell(0, [740, 659, 587, 523], 0.06),

  // Game moments
  // The organ's famous spooky opening, then a low minor chord and thunder.
  start: () => {
    [
      [880, 0, 0.06],
      [784, 0.09, 0.05],
      [880, 0.17, 0.35],
    ].forEach(([f, at, len]) => organ(at, f, len, 0.025));
    [147, 175, 220, 294].forEach((f) => organ(0.7, f, 0.7, 0.025));
    thunder(0.7, 0.18);
  },
  yourTurn: () => spell(0, [659, 784, 988, 1319, 1568], 0.06, 0.11),
  nextTurn: () => {
    chime(0, 784, 0.1, 0.4);
    chime(0.08, 587, 0.1, 0.5);
  },
  question: () => {
    chime(0, 659, 0.11, 0.4);
    chime(0.12, 932, 0.11, 0.5);
  },
  correct: () => {
    chime(0, 659);
    chime(0.1, 784);
    chime(0.2, 988, 0.13, 1.1);
    spell(0.2, [1976, 2489], 0.06, 0.05);
  },
  solvedMe: () => {
    spell(0, [523, 659, 784, 1047], 0.07, 0.1);
    [262, 330, 392, 523].forEach((f) => organ(0.3, f, 0.5, 0.022));
    spell(0.3, [1319, 1568, 2093, 2637], 0.06, 0.06);
  },
  // A ghost moaning down, over a low, sour organ chord.
  wrong: () => {
    ghost(0, 560, 220, 0.9, 0.13);
    organ(0, 110, 0.9, 0.025);
    organ(0, 117, 0.9, 0.02);
  },
  skip: () => ghost(0, 700, 300, 0.15, 0.11),
  // Thunder, then the organ's minor chord with a bell on top.
  reveal: () => {
    thunder(0, 0.3);
    [147, 220, 294, 349].forEach((f) => organ(0.25, f, 0.8, 0.022));
    chime(0.25, 1175, 0.08, 1.4);
  },
  // Rattling bones.
  roll: () => {
    [0, 0.05, 0.09, 0.16, 0.2, 0.28, 0.33, 0.42].forEach((at, i) => {
      noise(at, { filter: "bandpass", freq: 1300 + ((i * 433) % 900), q: 9, vol: 0.16 - i * 0.01, len: 0.03 });
      tone(at, 500 + ((i * 211) % 300), { type: "triangle", vol: 0.08, env: { d: 0.03, s: 0.0001, r: 0.01 } });
    });
    noise(0.52, { filter: "bandpass", freq: 900, q: 6, vol: 0.2, len: 0.05 });
  },
  // A creepy music box: two notes a little out of key.
  coin: () => {
    chime(0, 1568, 0.1, 0.4);
    chime(0.08, 1865, 0.11, 0.9);
  },
  // A ghost wailing up and down.
  siren: () => {
    ghost(0, 520, 780, 0.25, 0.12);
    ghost(0.4, 780, 520, 0.25, 0.12);
  },
  sparkle: () => spell(0, [1568, 1760, 1976, 2217, 2489], 0.04, 0.06),
  // Wind, then a wolf howling at the moon.
  night: () => {
    noise(0, { filter: "bandpass", freq: 400, to: 900, q: 0.8, vol: 0.12, env: { a: 0.5, d: 0.6, s: 0.3, r: 0.6 } });
    tone(0.3, 330, { type: "sine", vol: 0.15, to: 620, slide: 0.45, vib: [5, 6], env: { a: 0.12, d: 0.2, s: 0.8, hold: 0.9, r: 0.4 } });
    tone(0.3, 330, { type: "triangle", vol: 0.06, to: 620, slide: 0.45, vib: [5, 6], env: { a: 0.12, d: 0.2, s: 0.8, hold: 0.9, r: 0.4 } });
    tone(1.5, 620, { type: "sine", vol: 0.1, to: 440, env: { a: 0.02, d: 0.2, s: 0.6, hold: 0.2, r: 0.3 } });
  },
  // Dun, dun, DUNNN on the organ, with thunder on the last one.
  dun: () => {
    [
      [196, 0, 0.16],
      [185, 0.28, 0.16],
      [165, 0.56, 0.9],
    ].forEach(([f, at, len]) => {
      organ(at, f, len, 0.03);
      organ(at, f * 1.5, len, 0.02);
      kick(at, 0.35);
    });
    thunder(0.56, 0.22);
  },
  boom: () => {
    thunder(0, 0.4);
    kick(0, 0.4);
  },
  // An organ fanfare from minor to major, with bells climbing.
  podium: () => {
    [147, 220, 294, 349].forEach((f) => organ(0, f, 0.3, 0.02));
    [117, 175, 233, 294].forEach((f) => organ(0.4, f, 0.3, 0.02));
    [147, 220, 294, 370, 440].forEach((f) => organ(0.8, f, 0.9, 0.02));
    [1175, 1480, 1760, 2349].forEach((f, i) => chime(0.8 + i * 0.1, f, 0.08, 0.8));
    kick(0.8, 0.35);
  },
};

// Each holiday event's sounds, by event (see lib/season.js). The others are
// added from lib/eventSounds.js.
const EVENT_SETS = { halloween: HALLOWEEN };
export const addSoundSets = (sets) => Object.assign(EVENT_SETS, sets);

// Plays a sound made on the spot (see lib/newYear.js), unless sounds are off.
export function playRaw(fn) {
  if (muted || !ctx || ctx.state !== "running") return;
  fn();
}

export function playSound(name) {
  const sound = EVENT_SETS[currentEvent()]?.[name] || SOUNDS[name];
  if (muted || !sound || !ctx || ctx.state !== "running") return;
  sound();
}

// Which sound a tapped control makes. A data-sound attribute wins ("none"
// keeps it quiet); otherwise it depends on the kind of button.
const CLICK_SOUNDS = [
  [".ans-yes", "slideUp"],
  [".ans-no", "honk"],
  [".ans-maybe", "hmm"],
  [".chip, .reaction-btn, .emoji-choice, input[type=checkbox], input[type=radio]", "squeak"],
  [".btn-primary, .btn-secondary, .btn-lg, .btn-xl, .game-tab, .power-card", "boing"],
  [".btn-ghost, .icon-btn, .link", "bloop"],
];

export function clickSoundFor(el) {
  if (el.dataset.sound) return el.dataset.sound;
  return CLICK_SOUNDS.find(([selector]) => el.matches(selector))?.[1] || "pop";
}

if (typeof window !== "undefined") {
  // Browsers only let audio start from a click, tap or key press, and phones
  // can pause it again later, so every one of those (re)starts it.
  const unlock = () => {
    if (!ctx) {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (!AC) return;
      ctx = new AC();
    }
    if (ctx.state !== "running") ctx.resume().catch(() => {});
  };
  for (const e of ["pointerdown", "touchend", "keydown"]) window.addEventListener(e, unlock, { passive: true });

  // Every button in the app makes a sound. Runs after React's own click
  // handlers, so the mute button has already done its job.
  let lastClick = 0;
  window.addEventListener("click", (e) => {
    const el = e.target.closest?.("button, a.btn, [data-sound], input[type=checkbox], input[type=radio]");
    if (!el || el.disabled || performance.now() - lastClick < 60) return;
    lastClick = performance.now();
    playSound(clickSoundFor(el));
  });
}
